/* eslint-disable @typescript-eslint/unbound-method */
import React, { useEffect, ReactElement, FormEvent, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Helmet from 'react-helmet';
import {
  reduxForm,
  formValueSelector,
  Field,
  getFormValues,
  FieldArray,
} from 'redux-form/immutable';
import {
  Switch,
  Button,
  LinearProgress,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
} from '@material-ui/core';
import _ from 'lodash';

import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import Box from 'retired/Box';
import { selectUser } from 'entities/user/selectors';
import Cause, { CauseTypes } from 'entities/cause/model';
import { Privileges } from 'utils/privileges';
import FormTextField from 'retired/FormTextField';
import * as validators from 'utils/validators';
import FormCheckbox from 'retired/FormCheckbox';
import FormFileUpload from 'retired/FormFileUpload';
import FormSelect from 'retired/FormSelect';
import FormNonprofitsSelector from 'retired/FormNonprofitsSelector';
import Organization from 'entities/organization/model';
import DialogButton from 'retired/DialogButton';
import { Features } from 'utils/features';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';

import reducer from './reducer';
import epics from './epics';
import { selectLoading } from './selectors';
import { FORM_IDENTIFIER } from './constants';
import * as Actions from './actions';

const TypeSelector = (items: string[]) => (props: any) =>
  (
    <FormSelect fullWidth {...props}>
      {items.map((item) => (
        <MenuItem key={item} value={item}>
          {item}
        </MenuItem>
      ))}
    </FormSelect>
  );

const key = 'causeFormPage';

const required = validators.required();
const isHexColorValidator = validators.isHexColor();

const valueSelector = formValueSelector(FORM_IDENTIFIER);

interface Props {
  pristine: boolean;
  invalid: boolean;
  submitting: boolean;
  handleSubmit: (handler: (c: Cause) => void) => (e: FormEvent<HTMLFormElement>) => void;
  change: (name: string, value: any) => void;
}

const getAddedAndRemovedNonprofits = (
  formData: Cause,
  linkedNonprofits: Organization[] | null
): { addedNonprofits: string[]; removedNonprofits: string[] } => {
  if (!linkedNonprofits) {
    return { addedNonprofits: [], removedNonprofits: [] };
  }

  const originalNonprofitsIds = _.map(linkedNonprofits, 'id');
  const newNonprofitIds = _.map(formData.nonprofits, 'id');

  const addedNonprofits = _.difference(newNonprofitIds, originalNonprofitsIds);

  const removedNonprofits = _.difference(originalNonprofitsIds, newNonprofitIds);

  return { addedNonprofits, removedNonprofits };
};

const CauseFormPage = ({
  pristine,
  invalid,
  submitting,
  handleSubmit,
  change,
}: Props): ReactElement => {
  const {
    params: { id = '' },
  } = useRouteMatch();
  const isNew = id === 'new';
  const history = useHistory();
  const user = useSelector(selectUser);
  const loading = useSelector(selectLoading);
  const cause = useSelector(getFormValues(FORM_IDENTIFIER)) as Cause;
  const type = useSelector((state) => valueSelector(state, 'type'));
  const color = useSelector((state) => valueSelector(state, 'color'));
  const isActive = useSelector((state) => valueSelector(state, 'public'));
  const dispatch = useDispatch();

  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    dispatch(Actions.initAction({ id, cause }));
  }, [id, user]);

  const [linkedNonprofits, setLinkedNonprofits] = useState<Organization[] | null>(null);

  useEffect(() => {
    if (cause?.nonprofits && pristine) {
      setLinkedNonprofits(cause.nonprofits.toJS());
    }
  }, [cause]);

  const submit = (formData: Cause): void => {
    const data = formData.toJS();

    const { addedNonprofits, removedNonprofits } = getAddedAndRemovedNonprofits(
      data,
      linkedNonprofits
    );

    if (addedNonprofits.length) {
      data.addedNonprofits = addedNonprofits;
    }

    if (removedNonprofits.length) {
      data.removedNonprofits = removedNonprofits;
    }

    if (pristine) {
      history.replace('/');
    } else {
      // eslint-disable-next-line no-shadow
      const { type: newType, ...payload } = data;
      if (isNew) {
        payload.__t = newType;
      }
      dispatch(Actions.saveAction(payload));
    }
  };

  if (user && (!user.isEmployee || !user.hasPrivilege(Privileges.CAUSE_MANAGE))) {
    history.replace('/');
  }

  const title = type || 'Cause Area';
  const typeItems = ['Pillar']
    .concat(user?.hasFeature(Features.COMMUNITIES) ? [] : ['ERG'])
    .concat(user?.isAdmin ? ['Category', 'SDG'] : []);

  /**
   * @name: isDeedManagedCause
   * @NOTE: BI
   * Needed because Causes that are restricted (Owned by the platform) have translated text
   * We should only display the RTE component when it's a Cause that should be edited and can be displayed without translations
   */

  const isDeedManagedCause = cause && cause?.organization === undefined;

  const shouldShowRTE =
    !isDeedManagedCause || [CauseTypes.Pillar, CauseTypes.ERG].includes(cause?.type);

  return (
    <>
      {(loading || !user) && <LinearProgress />}
      {!loading && (
        <Box>
          <Helmet title={title} />
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <h1>{title}</h1>
            {!isNew && (
              <div>
                <Button
                  variant="outlined"
                  type="button"
                  onClick={() => {
                    dispatch(Actions.duplicateAction(cause.toJS()));
                  }}
                >
                  Duplicate Cause Area
                </Button>
              </div>
            )}
          </div>
          {submitting ? (
            <CircularProgress />
          ) : (
            <form onSubmit={handleSubmit(submit)}>
              <div
                style={{
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  padding: 30,
                  marginBottom: 30,
                }}
              >
                <div>
                  <Grid item xs={12} md={4}>
                    {isNew && (
                      <Field
                        name="type"
                        component={TypeSelector(typeItems)}
                        label="Cause Area type"
                      />
                    )}

                    <FormControlLabel
                      control={
                        <Switch
                          checked={isActive || false}
                          color="primary"
                          onChange={(e, checked) => change('public', checked)}
                          name="public"
                        />
                      }
                      label={isActive ? 'Active' : 'Inactive'}
                      style={{ width: 120, marginTop: 25 }}
                    />
                  </Grid>

                  <FormControl style={{ width: '100%', marginTop: 15, marginBottom: 15 }}>
                    <Field
                      name="name"
                      component={FormTextField}
                      label="Cause Area Name"
                      placeholder="Enter Cause Area Name"
                      validate={required}
                      required
                    />
                  </FormControl>
                  <Field
                    name="description"
                    component={shouldShowRTE ? FormDescriptionRteField : FormTextField}
                    inputProps={{ maxLength: 500 }}
                    fullWidth
                    multiline
                    label="Description *"
                    validate={required}
                  />
                  <div style={{ marginTop: 50 }}>
                    <Field
                      name="banner"
                      component={FormFileUpload}
                      labelText="Cause Area Banner (1440×436)"
                      maxSize={1024 * 1024 * 10}
                      cropRatio={1440 / 436}
                      quality={0.8}
                      fixed
                      imagePreview
                      withCrop
                    />
                  </div>
                  <div style={{ marginTop: 50 }}>
                    <Field
                      name="picture"
                      component={FormFileUpload}
                      labelText="Cause Area Picture (572 × 408)"
                      maxSize={1024 * 1024 * 10}
                      cropRatio={572 / 408}
                      quality={0.8}
                      fixed
                      imagePreview
                      withCrop
                    />
                  </div>

                  <div style={{ marginTop: 50, marginBottom: 30 }}>
                    <Grid container>
                      <Grid item xs={12} md={4}>
                        <div style={{ position: 'relative' }}>
                          <div
                            style={{
                              width: '20px',
                              height: '20px',
                              backgroundColor: color,
                              borderRadius: '3px',
                              border: '1px solid rgba(0, 0, 0, .5)',
                              position: 'absolute',
                              bottom: 11,
                              right: 0,
                            }}
                          />
                          <Field
                            name="color"
                            label="Color"
                            component={FormTextField}
                            inputProps={{ maxLength: 7 }}
                            fullWidth
                            placeholder="Please use a hex code (e.g. #FFFFFF)"
                            validate={isHexColorValidator}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </div>

                  <Field name="gradient" component={FormCheckbox} label="Gradient" />

                  {user?.isAdmin && (
                    <Field
                      name="individualsOnly"
                      component={FormCheckbox}
                      label="Individuals only"
                    />
                  )}

                  <div>
                    <Field
                      name="sorting"
                      component={FormTextField}
                      placeholder="e.g. 0"
                      label="Sorting"
                      helperText="Higher taking precedence"
                      type="number"
                    />
                  </div>

                  <div style={{ marginTop: 50 }}>
                    <FieldArray name="nonprofits" component={FormNonprofitsSelector} />
                  </div>
                </div>

                <div style={{ marginTop: 30 }}>
                  <Button type="submit" disabled={invalid} color="primary" variant="contained">
                    {
                      // eslint-disable-next-line no-nested-ternary
                      pristine ? 'Close' : isNew ? 'Submit' : 'Save'
                    }
                  </Button>

                  {!isNew && id === cause?.id && (
                    <DialogButton
                      style={{ display: 'inline-block', marginLeft: 30 }}
                      buttonLabel="Remove Cause Area"
                      title="Remove the cause"
                      text="Are you sure you want to remove this cause area? This action is irreversible"
                      confirmLabel="Remove"
                      cancelLabel="Go Back"
                      handleConfirm={() => {
                        dispatch(Actions.removeAction(id));
                      }}
                    />
                  )}
                </div>
              </div>
            </form>
          )}
        </Box>
      )}
    </>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(CauseFormPage);
